import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Routes, Route, Navigate as NavigateTo } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { ThemeProvider } from '@mui/material/styles';
import { StylesProvider, createGenerateClassName } from '@mui/styles';
// import CssBaseline from "@mui/material/CssBaseline";
import CssBaseline from '@mui/material/ScopedCssBaseline';

import SelectProject from './views/pages/SelectProject';
import BasicInformation from './views/pages/BasicInformation';
import TakePhoto from './views/pages/TakePhoto/TakePhoto';
import Registration from './views/pages/Registration';
import CreatePassword from './views/pages/CreatePassword';
import TermsAndConditions from './views/pages/termsAndConditions';
import StandardConsentForm from './views/pages/standardConsentForm';
import StandardBiometricConsent from './views/pages/standardBiometricConsent';
import RegistrationSignature from './views/pages/registrationSignature';
import PersonalInformation from './views/pages/PersonalInformation';
import Trades from './views/pages/Trades';
import ConsentForm from './views/pages/ConsentForm';
import ProjectList from './views/pages/ProjectList';
import MyCertifications from './views/pages/MyCertifications';
import MyDrugTesting from './views/pages/MyDrugTesting';
import Home from './Home'; //! Remove this later
import NotFoundScreen from './views/pages/NotFound';
import CacheBuster from './views/shared/CacheBuster';
import PublicRoute from './views/shared/PublicRoute';
import ContactInformation from './views/pages/ContactInformation';
import Certifications from './views/pages/Certifications';
import Login from './views/pages/Login';
import ForgotPassword from './views/pages/ForgotPassword';
import VerifyOtp from './views/pages/VerifyOtp';
import ConfirmResetPassword from './views/pages/ConfirmResetPassword';
import ConsentFormInformation from './views/pages/ConsentFormInformation';
import WorkerStatus from './views/pages/WorkerStatus';
import ImagePickerDemo from './views/pages/ImagePickerDemo';
import Courses from './views/pages/Courses';
import MyProfile from './views/pages/MyProfile';

import { setIsOnline } from './state-mgmt/app';
import { useAppDispatch, useAppSelector } from './state-mgmt/store';
import { authenticateUser, logoutUser } from './state-mgmt/user';
import useDataScheduler from 'hooks/useDataScheduler';
import usePiiVisibility from 'hooks/usePiiVisibility';
import useBackgroundUploader from 'hooks/useBackgroundUploader';
import useVerifyAdminContext from 'hooks/useVerifyAdminContext';
import useRouteGuard from 'hooks/useRouteGuard';

import { migrateAdminContextFromLocalStorage } from 'utils/adminContext';

import { ROUTES } from '../constants';
import { theme } from '../constants/style';

const AppRoot = () => {
  const { user, languageCode, isOnline } = useAppSelector(state => ({
    user: state.user,
    languageCode: state.app?.languageCode,
    isOnline: state.app?.isOnline,
  }));

  const [initialLoad, setInitialLoad] = useState(false);

  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  //* To observe and control PII field visibility
  usePiiVisibility();

  //* Initialize background upload system for admin users
  useBackgroundUploader();

  //* Initialize data scheduler for automatic refresh every 15 minutes
  const { isLoadingData } = useDataScheduler();

  //* Verify and restore saved admin project context after login
  const { verifyAndRestore } = useVerifyAdminContext();

  //* Global route guard — redirects based on auth state, role, and wizard step completion
  const { isRedirecting } = useRouteGuard({ enabled: initialLoad && !isLoadingData });

  const generateClassName = createGenerateClassName({
    productionPrefix: 'worker-portal',
  });

  const handleOnline = useCallback(() => {
    dispatch(setIsOnline(true));
  }, [dispatch]);

  const handleOffline = useCallback(() => {
    dispatch(setIsOnline(false));
  }, [dispatch]);

  const authenticateUserAndLoadData = async () => {
    try {
      // One-time migration of admin context from localStorage to IndexedDB
      //! Remove it after few days
      await migrateAdminContextFromLocalStorage();

      const authResult = await dispatch(authenticateUser({}))?.unwrap?.();
      if (authResult?.userId) {
        await verifyAndRestore();
      } else {
        dispatch(logoutUser({ sessionExpired: true }));
      }
    } catch (err) {
      user?.userId && dispatch(logoutUser({ sessionExpired: true }));
    } finally {
      setInitialLoad(true);
    }
  };

  useEffect(() => {
    if (navigator.onLine && isOnline) {
      authenticateUserAndLoadData();
    } else {
      setInitialLoad(true);
    }
  }, []);

  useEffect(() => {
    dispatch(setIsOnline(navigator.onLine));
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [dispatch, handleOnline, handleOffline]);

  useEffect(() => {
    i18n.changeLanguage(languageCode || 'en');
  }, [languageCode]);

  useEffect(() => {
    //* Set by the session-expired flow (store.ts) just before it reloads the app. Read here, not in
    //* Login: the route guard also mounts Login on the page being torn down, which would consume
    //* the flag before the reload and leave the fresh page without the toast.
    if (localStorage.getItem('sessionExpired')) {
      localStorage.removeItem('sessionExpired');
      enqueueSnackbar(t('common.sessionExpired'), { variant: 'error' });
    }
  }, []);

  useEffect(() => {
    //* To prevent auto zoom on iPhone devices when input is focused
    if (/(iPad|iPhone|iPod)/g.test(navigator.userAgent)) {
      document
        .querySelector('[name=viewport]')
        .setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
    }
  }, []);

  const redirectToHomeScreen = () => {
    if (user?.userId) {
      if (user?.isWorker) {
        return <NavigateTo to={ROUTES.PROJECT_LIST.path} replace />;
      } else {
        return <NavigateTo to={ROUTES.WORKER_STATUS.path} replace />;
      }
    } else {
      return <NavigateTo to={ROUTES.SELECT_PROJECT.path} replace />;
    }
  };

  return (
    <StylesProvider generateClassName={generateClassName} injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <CacheBuster />

        {initialLoad && !isLoadingData && !isRedirecting ? (
          <Fragment>
            <Routes>
              <Route path={'/'} element={redirectToHomeScreen()} />

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={'/components'} element={<Home />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.BASIC_INFORMATION.path} element={<BasicInformation />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.TAKE_PHOTO.path} element={<TakePhoto />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.REGISTRATION.path} element={<Registration />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.CREATE_PASSWORD.path} element={<CreatePassword />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.TERMS_AND_CONDITIONS.path} element={<TermsAndConditions />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.STANDARD_CONSENT_FORM.path} element={<StandardConsentForm />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.STANDARD_BIOMETRIC_CONSENT.path} element={<StandardBiometricConsent />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.REGISTRATION_SIGNATURE.path} element={<RegistrationSignature />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.PERSONAL_INFORMATION.path} element={<PersonalInformation />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.TRADES.path} element={<Trades />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.CERTIFICATIONS.path} element={<Certifications />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={'/test'} element={<BasicInformation />} />
              </Route>

              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.SELECT_PROJECT.path} element={<SelectProject />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.CONTACT_INFORMATION.path} element={<ContactInformation />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.CONSENT_FORM.path} element={<ConsentForm />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.PROJECT_LIST.path} element={<ProjectList />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.MY_COURSES.path} element={<Courses />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.MY_CERTIFICATIONS.path} element={<MyCertifications />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.MY_DRUG_TESTING.path} element={<MyDrugTesting />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.CONSENT_FORM_INFORMATION.path} element={<ConsentFormInformation />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.IMAGE_PICKER_DEMO.path} element={<ImagePickerDemo />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.LOGIN.path} element={<Login />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.FORGOT_PASSWORD.path} element={<ForgotPassword />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.VERIFY_OTP.path} element={<VerifyOtp />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.CONFIRM_RESET_PASSWORD.path} element={<ConfirmResetPassword />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.WORKER_STATUS.path} element={<WorkerStatus />} />
              </Route>
              <Route element={<PublicRoute noContainer={true} />}>
                <Route path={ROUTES.MY_PROFILE.path} element={<MyProfile />} />
              </Route>

              <Route path="*" element={<NotFoundScreen />} />
            </Routes>
          </Fragment>
        ) : (
          <CircularProgress
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              margin: 'auto',
            }}
          />
        )}
      </ThemeProvider>
    </StylesProvider>
  );
};

export default AppRoot;
